import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RouteComponent } from '@app/route/route.component';
import { StateService, ZaverProduct } from '@app/service/state/state.service';
import { HeaderService } from '@app/service/header/header.service';
import { UserService } from '@app/service/user-service/user.service';
import { SelectorItem } from '@app/component/selector/selector.component';
import { PaymentMethod } from '@app/model/payment-request/payment-request.dto';
import { AisAccountService, AisBankAccount, BankName } from '@app/service/ais-account/ais-account.service';

@Component({
  selector: 'se-confirm-in-house-onboarding',
  templateUrl: './se-confirm-in-house-onboarding.component.html',
  styleUrls: [
    '../../../page-shared.css',
    './se-confirm-in-house-onboarding.component.scss',
  ],
})
export class SeConfirmInHouseTokenizedOnboardingComponent extends RouteComponent implements OnInit {
  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private aisAccountService: AisAccountService
  ) {
    super(router, route, stateService, headerService);
  }

  directDebitTermsLink: string = 'https://cdn.zaver.com/SE/docs/zaver-se-autogiro-250828.pdf';

  accounts: AisBankAccount[] = null;
  selectedAccount: AisBankAccount = null;
  tempSelectedAccountId: string = null;
  showAccountSelection: boolean = false;
  loading: boolean = true;

  selectorItems: SelectorItem[] = null;
  termsItem: SelectorItem = null;
  modalActive: boolean = false;
  selectedItem: SelectorItem;

  async ngOnInit(): Promise<void> {
    // It is expected to have at least one valid mandate account at this point
    await this.loadAccounts();
    this.loading = false;


    this.selectorItems = [
      {
        tab: 'Betalningsvillkor',
        title: `${this.state.checkoutCustomizations.merchantName} villkor för betalning via Autogiro`,
        descriptions: [
          {
            title: 'Godkännande av betalningar',
            text: `${this.state.checkoutCustomizations.merchantName} erbjuder dig möjligheten att betala för dina köp via Autogiro. Genom att godkänna dessa villkor medger du att betalning för samtliga dina köp hos ${this.state.checkoutCustomizations.merchantName} får genomföras genom uttag från det konto som är anslutet till det autogiromedgivande som du lämnar till Zaver (se under rubrik “Administration”).`,
          },
          {
            title: 'Tidpunkt för autogirodebitering',
            text: `Vid betalning via Autogiro kommer pengarna att dras från ditt konto inom 1-2 bankdagar efter att hela eller delar av köpet har bekräftats. Om du har erbjudits periodvis betalning via Autogiro kommer det sammanlagda beloppet för samtliga dina köp hos ${this.state.checkoutCustomizations.merchantName} under innevarande period fram till uttagsdatumet i stället att dras från ditt konto vid ett samlat tillfälle enligt av ${this.state.checkoutCustomizations.merchantName} angiven faktureringscykel.`,
          },
          {
            title: 'Misslyckad autogirodebitering',
            text: 'Skulle täckning saknas på ditt konto när uttag ska genomföras kommer du istället att få en faktura på beloppet enligt dessa villkor. Fakturan ska betalas inom den tid som anges på fakturan. Vid betalning via faktura kan en administrationsavgift på upp till 49 kr tillkomma. Vid sen betalning av fakturan kan en påminnelseavgift om 60 kr tillkomma samt dröjsmålsränta på det förfallna beloppet till dess att full betalning sker. Dröjsmålsräntan utgår med 24 % per år. Sker ingen betalning därefter kan fordran komma att överlämnas för inkassohantering där ytterligare inkassokostnader kan tillkomma.',
          },
          {
            title: 'Kreditprövning',
            text: 'Notera att en kreditprövning görs när du ger ditt medgivande till betalning via Autogiro vilket i vissa fall innebär att en kreditupplysning inhämtas från extern part. Du får då en kopia på kreditupplysningen per fysisk och/eller digital post.',
          },
          {
            title: 'Administration',
            text: `Betalning via Autogiro och eventuell fakturering administreras av Zaver (Frink AB, org.nr 559059-8420) (“Zaver”) i egenskap av betaltjänstleverantör åt ${this.state.checkoutCustomizations.merchantName}. Genom att fortsätta lämnar du ett autogiromedgivande till Zaver i enlighet med villkoren för Autogiro.`,
          },
        ],
        links: [
          {
            text: 'Villkoren för Autogiro (pdf)',
            onClick: () => {
              window.open(
                this.directDebitTermsLink,
                '_blank'
              );
            },
          },
        ],
      },
    ];
  }

  async handleCtaClick() {
    try {
      this.loading = true;
      await this.userService.confirmTokenizedOnboardingInHouse(this.selectedAccount.id, this.directDebitTermsLink);
      await this.navigate();
      this.loading = false;
    } catch (error) {
      console.log(error);
      this.loading = false;
    }
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  showTerms(): void {
    this.selectedItem = this.termsItem;
    this.toggleModal();
  }

  changeAccount(): void {
    if (this.accounts && this.accounts.length > 1) {
      this.tempSelectedAccountId = this.selectedAccount?.id;
      this.showAccountSelection = !this.showAccountSelection;
    } else {
      this.selectNewAisAccount();
    }
  }

  onAccountRadioSelect(accountId: string): void {
    this.tempSelectedAccountId = accountId;
  }

  async handleAccountSelectionSubmit() {
    if (this.tempSelectedAccountId) {
      this.loading = true;
      try {
        await this.aisAccountService.selectAccount(this.tempSelectedAccountId);
        await this.loadAccounts();
        this.showAccountSelection = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  }

  async loadAccounts() {
    this.accounts = (await this.aisAccountService.getAccounts()).accounts;
    this.selectedAccount = this.accounts.find(acc => acc.isSelected)
      || this.accounts.find(acc => acc.isPrimary)
      || this.accounts[0];
  }

  async selectNewAisAccount() {
    await this.aisAccountService.resetAccountRequirement();
    this.navigate();
  }

  getMethodName(): string {
    if (
      this.state.zaverProduct === ZaverProduct.TOKENIZED_ONBOARDING &&
      this.state.paymentInformation.paymentMethod === PaymentMethod.COLLECTIVE_INVOICE
    ) {
      return 'månadsbetalning';
    }
    return 'direktbetalning';
  }

  hideAccountSelection(): void {
      this.showAccountSelection = false;
  }

  getBankDisplayName(bankKey: string): string {
    return BankName[bankKey];
  }
}
